/**
 * DispatchTopBar — Feb 2026 (Step 3 · Slice 2 · step 2/6)
 *
 * Breadcrumb strip that mounts globally at the app shell for dispatch/SA
 * routes. Shows `DOMAIN › PAGE` where the domain crumb is a tappable link
 * back to that domain's landing page.
 *
 * Sits ABOVE the tabbed content of each page but BELOW the existing
 * <Navbar> (which every dispatch page already renders). This is a nav shell
 * enhancement, not a replacement — no per-page changes required.
 *
 * On desktop we still render the breadcrumb (helps orientation even when
 * the left rail is visible), but the tap-to-parent behavior is most valuable
 * on mobile where there's no persistent sidebar.
 */
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { openGlobalUserSearch } from './GlobalUserSearch';
import { prefetchDomain, prefetchOrders, prefetchProducts } from '../lib/prefetch';

// Route → { domainKey, domainLabel, domainHref, pageLabel } lookup.
// Order matters — check more-specific paths before generic /dispatch.
// Kept in ONE place so BottomTabBar's activeTabKey stays in lockstep.
function resolveCrumbs(pathname) {
  const rules = [
    ['/dispatch/admin/reviews',       { key: 'reviews', label: 'REVIEWS', href: '/dispatch/admin/reviews',      page: 'MODERATION' }],
    ['/dispatch/admin/launch-stats',  { key: 'growth',  label: 'GROWTH',  href: '/dispatch/admin/launch-stats', page: 'LAUNCH STATS' }],
    ['/dispatch/admin/leaderboards',  { key: 'growth',  label: 'GROWTH',  href: '/dispatch/admin/launch-stats', page: 'LEADERBOARDS' }],
    ['/dispatch/admin/promo-agent',   { key: 'growth',  label: 'GROWTH',  href: '/dispatch/admin/launch-stats', page: 'PROMO AGENT' }],
    ['/dispatch/admin/boost',         { key: 'growth',  label: 'GROWTH',  href: '/dispatch/admin/launch-stats', page: 'BOOST' }],
    ['/dispatch/admin/users',         { key: 'people',  label: 'PEOPLE',  href: '/dispatch/admin/users',        page: 'USERS' }],
    ['/dispatch/admin/points',        { key: 'people',  label: 'PEOPLE',  href: '/dispatch/admin/users',        page: 'POINTS LEDGER' }],
    ['/dispatch/admin/edit-profile',  { key: 'system',  label: 'SYSTEM',  href: '/dispatch/settings',           page: 'EDIT PROFILE' }],
    ['/dispatch/admin/login-stats',   { key: 'system',  label: 'SYSTEM',  href: '/dispatch/settings',           page: 'LOGIN STATS' }],
    ['/dispatch/admin/push-health',   { key: 'system',  label: 'SYSTEM',  href: '/dispatch/settings',           page: 'PUSH HEALTH' }],
    ['/dispatch/admin/email-health',  { key: 'system',  label: 'SYSTEM',  href: '/dispatch/settings',           page: 'EMAIL HEALTH' }],
    ['/dispatch/admin/data-quality',  { key: 'ops',     label: 'OPS',     href: '/dispatch',                    page: 'DATA QUALITY' }],
    ['/dispatch/admin/failed-orders', { key: 'ops',     label: 'OPS',     href: '/dispatch',                    page: 'CHECKOUT REPORT' }],
    ['/dispatch/admin/regions',       { key: 'ops',     label: 'OPS',     href: '/dispatch',                    page: 'REGIONS' }],
    ['/dispatch/settings',            { key: 'system',  label: 'SYSTEM',  href: '/dispatch/settings',           page: 'SETTINGS' }],
    ['/dispatch/broadcast',           { key: 'ops',     label: 'OPS',     href: '/dispatch',                    page: 'BROADCAST' }],
  ];
  for (const [prefix, meta] of rules) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      // If we're AT the landing page of a domain, don't repeat DOMAIN › DOMAIN.
      const atDomainHome = pathname === meta.href;
      return { ...meta, atDomainHome };
    }
  }
  // Bare /dispatch = OPS home (no breadcrumb — you're already at the domain root)
  if (pathname === '/dispatch') return { key: 'ops', label: 'OPS', href: '/dispatch', page: null, atDomainHome: true };
  return null;
}

// Domain accent — kept in-sync with BottomTabBar for consistency
const ACCENT = {
  ops: '#00F0FF', growth: '#FF9F0A', reviews: '#FFD60A',
  people: '#39FF14', system: '#B87BFF',
};

export function DispatchTopBar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isDispatchScope = pathname.startsWith('/dispatch');
  const isAuthorized = user && (user.role === 'dispatcher' || user.role === 'super_admin');
  if (!isAuthorized || !isDispatchScope) return null;

  const crumbs = resolveCrumbs(pathname);
  // When at a bare domain home (no page crumb), we still render the bar so
  // the global-search button on the right stays reachable from every screen.
  const hasCrumbs = !!crumbs;
  const showCrumbs = hasCrumbs && !!crumbs.page && !crumbs.atDomainHome;

  const accent = hasCrumbs ? (ACCENT[crumbs.key] || '#39FF14') : '#39FF14';

  // Feb 2026 — Fluidity Instant Triggers. Warm the domain landing query
  // the millisecond a finger touches the crumb (before release), so the
  // destination page paints from cache. Idempotent — no-op if fresh.
  const warmDomain = () => {
    if (hasCrumbs) prefetchDomain(queryClient, crumbs.key);
  };
  // Warm the palette's ORDER/PRODUCT queries when the search launcher is
  // touched. Users pressing ⌘K almost always land on one of these two,
  // so pre-warming both is cheap and cuts perceived latency to zero.
  const warmSearch = () => {
    prefetchOrders(queryClient);
    prefetchProducts(queryClient);
  };

  // Feb 2026 hotfix — iOS PWA touch handling. The old `<Link>` had (a) no
  // padding so its ~15px tap target was well below iOS's 44px minimum, and
  // (b) `hover:underline` which triggers on first-tap on touch devices,
  // requiring a SECOND tap to actually navigate (the "hover-on-touch" bug).
  // Solution: an explicit onClick + navigate(), 44px min tap area, no hover
  // underline. When we're already AT the domain home the crumb renders as a
  // non-interactive span so users don't wonder why the tap did nothing.
  const handleDomainTap = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasCrumbs) return;
    if (pathname === crumbs.href) return; // already here — no-op
    navigate(crumbs.href);
  };

  return (
    <nav
      className="border-b border-[#2A0F3D] bg-[#0a0510]/95"
      aria-label="Breadcrumb"
      data-testid="dispatch-top-bar"
    >
      <div className="max-w-7xl mx-auto px-2 py-0.5 flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider min-h-[44px]">
        {hasCrumbs && (
          crumbs.atDomainHome ? (
            // At the domain landing — render as label, not a link.
            <span
              className="px-3 py-2.5"
              style={{ color: accent }}
              data-testid={`breadcrumb-domain-${crumbs.key}`}
            >
              {crumbs.label}
            </span>
          ) : (
            <Link
              to={crumbs.href}
              onClick={handleDomainTap}
              onPointerDown={warmDomain}
              onTouchStart={warmDomain}
              onPointerEnter={warmDomain}
              onFocus={warmDomain}
              className="px-3 py-2.5 -my-1 transition-colors active:opacity-70 select-none"
              style={{ color: accent, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
              data-testid={`breadcrumb-domain-${crumbs.key}`}
            >
              {crumbs.label}
            </Link>
          )
        )}
        {showCrumbs && (
          <>
            <ChevronRight className="w-3 h-3 text-[#4B6B52] shrink-0" />
            <span
              className="text-[#A3C7A8] px-1 py-2.5"
              data-testid="breadcrumb-page"
            >
              {crumbs.page}
            </span>
          </>
        )}
        {/* Feb 2026 (Slice 2 · step 3) — global user-search launcher.
            Tap on mobile, or ⌘K / Ctrl+K anywhere on desktop. */}
        <button
          type="button"
          onClick={openGlobalUserSearch}
          onPointerDown={warmSearch}
          onTouchStart={warmSearch}
          onPointerEnter={warmSearch}
          onFocus={warmSearch}
          className="ml-auto flex items-center gap-1 px-3 py-2 border border-[#2A0F3D] text-[#A3C7A8] hover:text-[#39FF14] hover:border-[#39FF14] active:opacity-70 transition-colors min-h-[36px]"
          style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
          data-testid="global-user-search-trigger"
          aria-label="Search users"
        >
          <Search className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">SEARCH</span>
          <span className="hidden md:inline text-[#4B6B52] normal-case tracking-normal">⌘K</span>
        </button>
      </div>
    </nav>
  );
}

export default DispatchTopBar;
